import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type {
	SelectFilterOption,
	SelectFilterMonad,
} from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys } from '../../../../../common/constants/product-keys';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createSingleWithCustomOptionSelectFilter } from '../../../../filters/single-select-with-custom-option-filter';
import { closeDateLabels } from './messages';

const CLOSE_DATE_VALUES = [
	'anytime',
	'today',
	'last7days',
	'last30days',
	'last90days',
	'last365days',
	'next7days',
	'next30days',
	'next90days',
	'next365days',
	'custom',
] as const;

const isIsoDate = (str?: string) => {
	if (!str) {
		return false;
	}
	if (!/\d{4}-\d{2}-\d{2}/.test(str)) {
		return false;
	}
	const d = new Date(str);
	return !isNaN(d.getTime());
};

const salesforceProducts: ProductKeys[] = [ProductKeys.Salesforce];
const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('closeDate');
const closeDateProducts: ProductKeys[] = Array.from(
	new Set<ProductKeys>([...salesforceProducts, ...products3PFromConfig]),
);

export const filter: Readonly<{
	id: 'closeDate';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => Readonly<{
			id: 'closeDate';
			type: 'single-select' | 'multi-select';
			options: SelectFilterOption[];
			didMountPerProducts: Record<string, boolean>;
			hasSelection: boolean;
			alwaysVisible: boolean;
			universal: boolean;
			value: string[];
			selectedOptions: SelectFilterOption[];
			queryParams: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			initialOptions: {
				options: SelectFilterOption[];
				isLoading: boolean;
			};
			lookup: {
				options: SelectFilterOption[];
				lookupResultOptions: SelectFilterOption[];
				input: string;
				isLoading: boolean;
				isActive: boolean;
				isDefined: boolean;
			};
			products: ProductKeys[];
			customValue?: Record<string, string>;
			customValueFields?: string[];
		}>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (state: unknown, config: CloudConfig) => SelectFilterMonad<'closeDate'>;
	getInitialState: (config: CloudConfig) => SelectFilterMonad<'closeDate'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createSingleWithCustomOptionSelectFilter({
	id: 'closeDate' as const,
	products: closeDateProducts,
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'closeDate',
		fetcher: async (values, config) => {
			return CLOSE_DATE_VALUES.filter((value) => values.includes(value)).map((value) => ({
				trackingKey: value,
				label:
					config.intl?.formatMessage(closeDateLabels[value]) ||
					closeDateLabels[value].defaultMessage,
				value,
				queryParamValue: value,
				product: config.product,
			}));
		},
		customValueParser: (queryParams) => {
			const { closeDateVal } = queryParams;
			const isDateValid = isIsoDate(closeDateVal?.[0]);
			const dateVal = isDateValid && closeDateVal?.[0] ? closeDateVal[0] : '';
			if (dateVal) {
				return { closeDateVal: dateVal };
			}
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return CLOSE_DATE_VALUES.map((value) => ({
				trackingKey: value,
				label:
					config.intl?.formatMessage(closeDateLabels[value]) ||
					closeDateLabels[value].defaultMessage,
				value,
				queryParamValue: value,
				product: config.product,
			}));
		},
	},
	customValueFields: ['closeDateVal'],
});
